import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { authService } from "../services/auth";
import { FaSpinner, FaDownload } from "../utils/icons";
import { Button } from "@/design-system/components/react";

// ── wire types (mirror src/study_endpoints.py admin endpoints) ──────────────
type Arm = "with_ai" | "without_ai" | null;

interface SessionCase {
  reader: string;
  study_uid: string;
  arm: Arm;
  signed: boolean;
  active_reading_ms: number | null;
  difficulty: number | null;
  time_discrepancy: boolean;
  has_initial: boolean;
}

interface Kpis {
  participants: number;
  expected_per_reader: number;
  expected_total: number;
  signed: number;
  mean_active_ms_with_ai: number | null;
  mean_active_ms_without_ai: number | null;
  n_signed_with_ai: number;
  n_signed_without_ai: number;
}

interface SessionsResponse {
  schedule_id: string;
  kpis: Kpis;
  cases: SessionCase[];
  fetched_at: string;
  cached: boolean;
  ttl_s: number;
}

interface ReportResponse {
  reader: string;
  study_uid: string;
  initial_html: string | null;
  final_html: string | null;
  signature: { signed_at?: string; difficulty_self_rated?: number } | null;
}

const POLL_MS = 30_000;

const sessionsKey = (projectId: string) => ["reader-study-sessions", projectId] as const;

function authHeader() {
  const { token } = authService.getStoredAuth();
  return { headers: { Authorization: `Bearer ${token}` } };
}

async function fetchSessions(projectId: string, force: boolean): Promise<SessionsResponse> {
  const q = force ? "?refresh=true" : "";
  const resp = await axios.get(
    `${getApiBaseUrl()}/api/study/projects/${projectId}/sessions${q}`,
    authHeader(),
  );
  return resp.data;
}

async function fetchReport(
  projectId: string,
  reader: string,
  studyUid: string,
): Promise<ReportResponse> {
  const params = new URLSearchParams({ reader, study_uid: studyUid });
  const resp = await axios.get(
    `${getApiBaseUrl()}/api/study/projects/${projectId}/report?${params}`,
    authHeader(),
  );
  return resp.data;
}

export function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

type ArmStyle = { text: string; cls: string };

// Exhaustive over the real arms — adding a value to Arm forces a key here.
const ARM_LABELS: Record<Exclude<Arm, null>, ArmStyle> = {
  with_ai: { text: "AI", cls: "bg-signal-tint text-signal-text-tint border-signal-stroke-soft" },
  without_ai: { text: "blank", cls: "bg-surface-2 text-fg-muted border-border" },
};
const UNKNOWN_ARM: ArmStyle = {
  text: "?",
  cls: "bg-surface text-fg-faint border-border",
};

function armLabel(arm: Arm): ArmStyle {
  return arm ? ARM_LABELS[arm] : UNKNOWN_ARM;
}

function shortUid(uid: string): string {
  return `…${uid.slice(-8)}`;
}

function secsAgo(iso: string, nowMs: number): number {
  return Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 1000));
}

// ── plain-text diff: HTML → text lines → LCS line diff ──────────────────────
export function htmlToLines(html?: string | null): string[] {
  if (!html) return [];
  const withBreaks = html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li|tr|section)>/gi, "\n");
  const ta = document.createElement("textarea");
  ta.innerHTML = withBreaks.replace(/<[^>]+>/g, "");
  return ta.value
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

export type DiffLine = { type: "ctx" | "add" | "del"; text: string };
export function lineDiff(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => Array.from({ length: m + 1 }, () => 0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "ctx", text: a[i]! });
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      out.push({ type: "del", text: a[i]! });
      i++;
    } else {
      out.push({ type: "add", text: b[j]! });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: a[i++]! });
  while (j < m) out.push({ type: "add", text: b[j++]! });
  return out;
}

// Server-built zip of per-(reader, case) {note, rows} JSON files — the raw
// a2z-medi-flow session rows in the research-repo analysis ingestion contract,
// to commit to the repo.
async function downloadRepoBundle(projectId: string): Promise<void> {
  const resp = await axios.get(`${getApiBaseUrl()}/api/study/projects/${projectId}/export`, {
    ...authHeader(),
    responseType: "blob",
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `reader-study-${projectId}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-surface-2 border border-border rounded-frame p-4">
      <div className="text-xs uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="text-2xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-sm text-fg-muted">{sub}</div>}
    </div>
  );
}

// ── report drawer ───────────────────────────────────────────────────────────
type ReportTab = "diff" | "initial" | "final";

function TabButton({
  id,
  label,
  active,
  onSelect,
}: {
  id: ReportTab;
  label: string;
  active: boolean;
  onSelect: (id: ReportTab) => void;
}) {
  return (
    <button
      onClick={() => onSelect(id)}
      className={`px-3 py-1.5 text-sm font-semibold rounded-t ${
        active ? "bg-bg-2 text-fg border border-b-0 border-border" : "text-fg-muted hover:text-fg"
      }`}
    >
      {label}
    </button>
  );
}

function ReportDrawer({ projectId, c }: { projectId: string; c: SessionCase }) {
  const [tab, setTab] = useState<ReportTab>("diff");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["reader-study-report", projectId, c.reader, c.study_uid],
    queryFn: () => fetchReport(projectId, c.reader, c.study_uid),
    staleTime: 5 * 60_000,
  });

  // O(n·m) LCS — only recompute when the two reports actually change, not on
  // every render of an open drawer (tab switches, parent poll re-renders).
  const diff = useMemo(
    () => lineDiff(htmlToLines(data?.initial_html), htmlToLines(data?.final_html)),
    [data?.initial_html, data?.final_html],
  );

  if (isLoading) return <div className="p-4 text-fg-muted text-sm">Loading report…</div>;
  if (isError || !data) return <div className="p-4 text-warn text-sm">Failed to load report.</div>;

  return (
    <div className="border border-signal-stroke-soft rounded bg-bg-2">
      <div className="flex items-center gap-1 px-2 pt-2">
        <TabButton id="diff" label="Diff" active={tab === "diff"} onSelect={setTab} />
        <TabButton id="initial" label="Initial" active={tab === "initial"} onSelect={setTab} />
        <TabButton id="final" label="Final" active={tab === "final"} onSelect={setTab} />
        {data.signature?.signed_at && (
          <span className="ml-auto pr-2 text-xs text-fg-dim">
            signed {new Date(data.signature.signed_at).toLocaleString()}
          </span>
        )}
      </div>
      <div className="p-3 bg-bg rounded-b text-sm">
        {tab === "diff" && (
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">
            {diff.map((d, idx) => (
              <span
                key={idx}
                className={
                  d.type === "add"
                    ? "block bg-signal-tint text-signal-text-tint"
                    : d.type === "del"
                      ? "block text-fg-faint line-through"
                      : "block text-fg-muted"
                }
              >
                {d.type === "add" ? "+ " : d.type === "del" ? "− " : "  "}
                {d.text}
              </span>
            ))}
          </pre>
        )}
        {(tab === "initial" || tab === "final") && (
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-fg">
            {htmlToLines(tab === "initial" ? data.initial_html : data.final_html).join("\n") || "—"}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function ReaderStudyLiveData() {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [auto, setAuto] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const forceRef = useRef(false);
  const queryClient = useQueryClient();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const downloadBundle = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadRepoBundle(projectId);
    } catch (err) {
      console.error("reader-study repo-bundle export failed", err);
      setExportError("Repo export failed — retry.");
    } finally {
      setExporting(false);
    }
  }, [projectId]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: sessionsKey(projectId),
    queryFn: () => {
      const force = forceRef.current;
      forceRef.current = false;
      return fetchSessions(projectId, force);
    },
    refetchInterval: auto ? POLL_MS : false,
  });

  // Cancel any in-flight poll first so the forced (cache-busting) fetch isn't
  // deduped into a background refresh=false request already on the wire.
  const manualRefresh = useCallback(() => {
    forceRef.current = true;
    void queryClient.cancelQueries({ queryKey: sessionsKey(projectId) }).then(() => refetch());
  }, [queryClient, projectId, refetch]);

  // "updated Ns ago" must keep counting up between 30s polls — drive it off a
  // 1s tick, not the query data, so a live monitor doesn't sit frozen at ≈0s.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const updatedAgo = data ? secsAgo(data.fetched_at, nowMs) : null;

  if (isLoading) {
    return (
      <div className="p-8 text-fg-muted flex items-center gap-2">
        <FaSpinner className="animate-spin" /> Loading reader-study data…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-8 text-warn">Failed to load reader-study sessions for {projectId}.</div>
    );
  }

  const k = data.kpis;

  return (
    <div className="max-w-6xl mx-auto p-6 text-fg">
      <Link to="/admin/reader-study" className="text-sm text-fg-muted hover:text-signal-text-tint">
        ← All reader studies
      </Link>
      <div className="flex items-center gap-3 flex-wrap mb-4 mt-2">
        <h1 className="text-2xl font-bold">Reader study — live data</h1>
        <span className="text-sm text-fg-muted font-mono">{data.schedule_id}</span>
        <span className="flex-1" />
        <span className="inline-flex items-center gap-1.5 text-xs text-signal-text-tint">
          <span className="w-2 h-2 rounded-full bg-signal shadow-[0_0_8px] shadow-signal" /> live
        </span>
        <label className="text-sm text-fg-muted flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto
          (30s)
        </label>
        <Button variant="secondary" onClick={manualRefresh} disabled={isFetching}>
          {isFetching ? <FaSpinner className="animate-spin" /> : "↻"} Refresh
        </Button>
        <span className="text-sm text-fg-muted">
          {updatedAgo == null ? "" : `updated ${updatedAgo}s ago`}
          {data.cached ? " (cached)" : ""}
        </span>
        <Button variant="secondary" onClick={downloadBundle} disabled={exporting}>
          {exporting ? <FaSpinner className="animate-spin" /> : <FaDownload />} Repo bundle (.zip)
        </Button>
        {exportError && <span className="text-sm text-warn">{exportError}</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Kpi
          label="Participants"
          value={String(k.participants)}
          sub={`${k.expected_per_reader} cases/reader`}
        />
        <Kpi label="Reader-cases signed" value={`${k.signed} / ${k.expected_total}`} />
        <Kpi
          label="Mean reading time"
          value={`AI ${fmtMs(k.mean_active_ms_with_ai)}`}
          sub={`Blank ${fmtMs(k.mean_active_ms_without_ai)} · n=${k.n_signed_with_ai}/${k.n_signed_without_ai}`}
        />
      </div>

      <div className="overflow-hidden border border-border rounded-frame">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 text-fg-muted text-xs uppercase tracking-wide">
              <th className="text-left px-3 py-2.5">Reader</th>
              <th className="text-left px-3 py-2.5">Case</th>
              <th className="text-left px-3 py-2.5">Arm</th>
              <th className="text-left px-3 py-2.5">Time</th>
              <th className="text-left px-3 py-2.5">Signed</th>
              <th className="text-left px-3 py-2.5">Diff.</th>
              <th className="text-left px-3 py-2.5">Report</th>
            </tr>
          </thead>
          <tbody>
            {data.cases.map((c) => {
              const key = `${c.reader}|${c.study_uid}`;
              const arm = armLabel(c.arm);
              const isOpen = expanded === key;
              return (
                <Fragment key={key}>
                  <tr className="border-t border-border hover:bg-signal-ambient">
                    <td className="px-3 py-2.5">{c.reader.replace("@a2zradiology.ai", "")}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{shortUid(c.study_uid)}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${arm.cls}`}
                      >
                        {arm.text}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {fmtMs(c.active_reading_ms)}
                      {c.time_discrepancy && (
                        <span className="ml-1 text-warn" title="active-time discrepancy flag">
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {c.signed ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-signal-tint text-signal-text-tint border border-signal-stroke-soft">
                          signed
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-surface-2 text-fg-muted border border-border">
                          in progress
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">{c.difficulty ?? "—"}</td>
                    <td className="px-3 py-2.5">
                      <button
                        disabled={!c.signed}
                        onClick={() => setExpanded(isOpen ? null : key)}
                        className="text-xs px-2 py-1 rounded border border-signal-stroke-soft text-signal-text-tint disabled:opacity-30 disabled:cursor-not-allowed hover:bg-signal-ambient"
                      >
                        {isOpen ? "Hide ▴" : "View ▾"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} className="px-3 py-2 bg-transparent">
                        <ReportDrawer projectId={projectId} c={c} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-fg-dim mt-3">
        Arm A = AI draft · B = blank template · time = active reading (idle-paused) · ⚠ =
        active-time discrepancy. Confidence/mental-demand columns pending (separate survey capture).
      </p>
    </div>
  );
}
